import axios from '@/utils/axios';
import {
  InvoiceDryRun,
  InvoiceDryRunIssue,
  InvoiceDryRunProviderInvoiceLineItem,
  InvoiceDryRunProviderInvoicePreview,
  InvoiceDryRunStatus,
  InvoiceRun,
  PaginatedResponse,
  PaginationMeta,
  ProviderInvoice,
} from '../types';

type UnknownRecord = Record<string, unknown>;

const toRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};

const firstDefined = (...values: unknown[]) =>
  values.find((value) => value !== undefined && value !== null);

const toString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const toId = (value: unknown, fallback: number | string): number | string => {
  const numberValue = toNumber(value);
  if (numberValue !== null) return numberValue;
  const stringValue = toString(value);
  return stringValue ?? fallback;
};

const toArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

const normalizeStatus = (value: unknown): InvoiceDryRunStatus => {
  const raw =
    typeof value === 'string'
      ? value
      : toString(toRecord(value).value) ?? toString(toRecord(value).display);

  const normalized = raw?.toUpperCase();
  if (
    normalized === 'QUEUED' ||
    normalized === 'RUNNING' ||
    normalized === 'COMPLETED' ||
    normalized === 'FAILED'
  ) {
    return normalized;
  }

  return 'QUEUED';
};

const extractEntity = (payload: unknown): unknown => {
  const root = toRecord(payload);
  const data = toRecord(root.data);

  return data.data ?? root.data ?? root;
};

const extractCollection = (payload: unknown): unknown[] => {
  const root = toRecord(payload);
  const data = toRecord(root.data);

  const candidates = [
    root.data,
    data.data,
    root.items,
    data.items,
    root.results,
    data.results,
    root.dryRuns,
    data.dryRuns,
    root.issues,
    data.issues,
    root.providerInvoices,
    data.providerInvoices,
    root.provider_invoices,
    data.provider_invoices,
  ];

  return (candidates.find(Array.isArray) as unknown[] | undefined) ?? [];
};

const normalizePaginationMeta = (payload: unknown): PaginationMeta => {
  const root = toRecord(payload);
  const data = toRecord(root.data);
  const meta = toRecord(firstDefined(root.meta, data.meta));
  const pagination = toRecord(firstDefined(root.pagination, data.pagination));
  const source = { ...root, ...data, ...pagination, ...meta };

  return {
    total:
      toNumber(
        firstDefined(
          source.total,
          source.total_count,
          source.totalCount,
          source.count
        )
      ) ?? null,
    page:
      toNumber(
        firstDefined(source.page, source.current_page, source.pageNumber)
      ) ?? null,
    limit:
      toNumber(firstDefined(source.limit, source.per_page, source.pageSize)) ??
      null,
    totalPages:
      toNumber(
        firstDefined(
          source.totalPages,
          source.total_pages,
          source.last_page,
          source.pages
        )
      ) ?? null,
  };
};

const normalizeDryRun = (value: unknown, index = 0): InvoiceDryRun => {
  const item = toRecord(value);
  const requestedBy = firstDefined(
    item.requested_by,
    item.requestedBy,
    item.requested_user,
    item.requestedUser,
    item.user
  );

  return {
    ...(item as Partial<InvoiceDryRun>),
    id: toNumber(item.id) ?? index,
    uuid: toString(item.uuid),
    status: normalizeStatus(item.status),
    date_from: toString(
      firstDefined(
        item.date_from,
        item.dateFrom,
        item.start_date,
        item.startDate
      )
    ),
    date_to: toString(
      firstDefined(item.date_to, item.dateTo, item.end_date, item.endDate)
    ),
    requested_by:
      typeof requestedBy === 'string' || typeof requestedBy === 'object'
        ? (requestedBy as InvoiceDryRun['requested_by'])
        : null,
    total_jobs: toNumber(firstDefined(item.total_jobs, item.totalJobs)),
    processed_jobs: toNumber(
      firstDefined(item.processed_jobs, item.processedJobs)
    ),
    issue_count: toNumber(
      firstDefined(
        item.issue_count,
        item.issueCount,
        item.issues_count,
        item.issuesCount
      )
    ),
    provider_invoice_count: toNumber(
      firstDefined(
        item.provider_invoice_count,
        item.providerInvoiceCount,
        item.provider_invoices_count,
        item.providerInvoicesCount
      )
    ),
    error_message: toString(
      firstDefined(item.error_message, item.errorMessage, item.error)
    ),
    created_at: toString(firstDefined(item.created_at, item.createdAt)),
  };
};

const normalizeIssue = (value: unknown, index = 0): InvoiceDryRunIssue => {
  const item = toRecord(value);

  return {
    ...(item as Partial<InvoiceDryRunIssue>),
    id: toId(item.id, `dry-run-issue-${index}`),
    type: toString(item.type),
    description: toString(item.description),
    details: item.details,
    provider_id:
      toId(firstDefined(item.provider_id, item.providerId), '') || null,
    booking_id: toId(firstDefined(item.booking_id, item.bookingId), '') || null,
    session_id: toId(firstDefined(item.session_id, item.sessionId), '') || null,
    link: toString(item.link),
  };
};

const normalizeUserSummary = (
  value: unknown,
  fallbackUserId: number | string | null
): InvoiceDryRunProviderInvoicePreview['provider'] => {
  const item = toRecord(value);
  if (!Object.keys(item).length) return null;

  const id =
    toNumber(firstDefined(item.id, item.user_id, item.userId)) ??
    toNumber(fallbackUserId);
  const firstName = toString(firstDefined(item.first_name, item.firstName));
  const lastName = toString(firstDefined(item.last_name, item.lastName));
  const email = toString(item.email);
  const profileImage = toString(
    firstDefined(
      item.profile_image,
      item.profileImage,
      item.avatar,
      item.avatar_url,
      item.avatarUrl
    )
  );

  return {
    ...(item as InvoiceDryRunProviderInvoicePreview['provider']),
    ...(id !== null ? { id } : {}),
    ...(firstName !== null ? { first_name: firstName } : {}),
    ...(lastName !== null ? { last_name: lastName } : {}),
    ...(email !== null ? { email } : {}),
    ...(profileImage !== null ? { profile_image: profileImage } : {}),
  };
};

const normalizeLineItem = (
  value: unknown,
  index = 0
): InvoiceDryRunProviderInvoiceLineItem => {
  const item = toRecord(value);
  const bookingSession = toRecord(
    firstDefined(
      item.booking_session,
      item.bookingSession,
      item.booking_sessions
    )
  );
  const booking = toRecord(firstDefined(bookingSession.booking, item.booking));
  const clientId =
    toId(
      firstDefined(item.client_id, item.clientId, item.user_id, item.userId),
      ''
    ) || null;
  const client =
    normalizeUserSummary(
      firstDefined(
        item.client,
        item.client_user,
        item.clientUser,
        item.user,
        item.users,
        booking.user,
        booking.client
      ),
      clientId
    ) ??
    normalizeUserSummary(
      {
        id: clientId,
        first_name: firstDefined(
          item.client_name,
          item.clientName,
          item.client_full_name,
          item.clientFullName
        ),
        profile_image: firstDefined(
          item.client_profile_image,
          item.clientProfileImage
        ),
      },
      clientId
    );

  return {
    ...(item as Partial<InvoiceDryRunProviderInvoiceLineItem>),
    id: toId(item.id, `dry-run-line-item-${index}`),
    job_id: toId(firstDefined(item.job_id, item.jobId), '') || null,
    client_id: clientId,
    client,
    description: toString(item.description),
    job_timestamp: toString(
      firstDefined(item.job_timestamp, item.jobTimestamp)
    ),
    hours: firstDefined(item.hours, null) as number | string | null,
    price_per_hour: firstDefined(item.price_per_hour, item.pricePerHour) as
      | number
      | string
      | null,
    subtotal: firstDefined(item.subtotal, null) as number | string | null,
    cleaning_products: firstDefined(
      item.cleaning_products,
      item.cleaningProducts
    ) as number | string | null,
    total: firstDefined(item.total, null) as number | string | null,
  };
};

const normalizeProviderUser = (
  value: unknown,
  fallbackProviderId: number | string | null
) => normalizeUserSummary(value, fallbackProviderId);

const normalizeProviderPreview = (
  value: unknown,
  index = 0
): InvoiceDryRunProviderInvoicePreview => {
  const item = toRecord(value);
  const rawLineItems = toArray(
    firstDefined(
      item.line_items,
      item.lineItems,
      item.provider_invoice_items,
      item.providerInvoiceItems,
      item.items
    )
  );
  const countRecord = toRecord(item._count);
  const providerId =
    toId(
      firstDefined(
        item.provider_id,
        item.providerId,
        item.user_id,
        item.userId
      ),
      ''
    ) || null;
  const provider =
    normalizeProviderUser(
      firstDefined(
        item.provider,
        item.provider_user,
        item.providerUser,
        item.user,
        item.users,
        item.cleaner
      ),
      providerId
    ) ??
    normalizeProviderUser(
      {
        id: providerId,
        first_name: firstDefined(
          item.provider_name,
          item.providerName,
          item.provider_full_name,
          item.providerFullName,
          item.cleaner_name,
          item.cleanerName
        ),
        profile_image: firstDefined(
          item.provider_profile_image,
          item.providerProfileImage,
          item.cleaner_profile_image,
          item.cleanerProfileImage
        ),
      },
      providerId
    );

  return {
    ...(item as Partial<InvoiceDryRunProviderInvoicePreview>),
    id: toNumber(item.id) ?? index,
    provider_id: providerId,
    provider,
    date_from: toString(firstDefined(item.date_from, item.dateFrom)),
    date_to: toString(firstDefined(item.date_to, item.dateTo)),
    total: firstDefined(item.total, null) as number | string | null,
    base_total: firstDefined(item.base_total, item.baseTotal) as
      | number
      | string
      | null,
    adjustments_total: firstDefined(
      item.adjustments_total,
      item.adjustmentsTotal
    ) as number | string | null,
    adjusted_total: firstDefined(item.adjusted_total, item.adjustedTotal) as
      | number
      | string
      | null,
    item_count:
      toNumber(
        firstDefined(
          item.item_count,
          item.itemCount,
          countRecord.provider_invoice_items,
          countRecord.line_items
        )
      ) ?? rawLineItems.length,
    line_items: rawLineItems.map(normalizeLineItem),
  };
};

const normalizeListResponse = <T>(
  payload: unknown,
  normalizer: (value: unknown, index: number) => T
): PaginatedResponse<T> => {
  const data = extractCollection(payload).map(normalizer);
  const meta = normalizePaginationMeta(payload);

  return {
    data,
    meta: {
      ...meta,
      total: meta.total ?? data.length,
    },
  };
};

export async function getAllInvoiceRuns(): Promise<InvoiceRun[]> {
  const {
    data: { data },
  } = await axios.get(`/invoices/run`);
  return data;
}

export type GetInvoiceDryRunsParams = Partial<{
  page: number;
  limit: number;
  status: InvoiceDryRunStatus;
}>;

export type GetInvoiceDryRunIssuesParams = Partial<{
  page: number;
  limit: number;
  type: string;
}>;

export type GetInvoiceDryRunProviderInvoicesParams = Partial<{
  page: number;
  limit: number;
  providerId: number | string;
}>;

export async function getInvoiceDryRuns(
  params?: GetInvoiceDryRunsParams
): Promise<PaginatedResponse<InvoiceDryRun>> {
  const response = await axios.get('/invoices/dry-runs', {
    params,
  });

  return normalizeListResponse(response.data, normalizeDryRun);
}

export async function getInvoiceDryRun(
  id: number | string
): Promise<InvoiceDryRun> {
  const response = await axios.get(`/invoices/dry-runs/${id}`);

  return normalizeDryRun(extractEntity(response.data));
}

export async function getInvoiceDryRunIssues(
  id: number | string,
  params?: GetInvoiceDryRunIssuesParams
): Promise<PaginatedResponse<InvoiceDryRunIssue>> {
  const response = await axios.get(`/invoices/dry-runs/${id}/issues`, {
    params,
  });

  return normalizeListResponse(response.data, normalizeIssue);
}

export async function getInvoiceDryRunProviderInvoices(
  id: number | string,
  params?: GetInvoiceDryRunProviderInvoicesParams
): Promise<PaginatedResponse<InvoiceDryRunProviderInvoicePreview>> {
  const response = await axios.get(
    `/invoices/dry-runs/${id}/provider-invoices`,
    {
      params,
    }
  );

  return normalizeListResponse(response.data, normalizeProviderPreview);
}

export async function getInvoiceDryRunProviderInvoice(
  id: number | string,
  invoiceId: number | string
): Promise<InvoiceDryRunProviderInvoicePreview> {
  const response = await axios.get(
    `/invoices/dry-runs/${id}/provider-invoices/${invoiceId}`
  );

  return normalizeProviderPreview(extractEntity(response.data));
}

export async function getAllProviderInvoicesForRun(
  id: number
): Promise<ProviderInvoice[]> {
  const {
    data: { data },
  } = await axios.get(`/invoices/provider/run/${id}`);
  return data;
}

export async function getProviderInvoices(
  provider_id: number
): Promise<ProviderInvoice[]> {
  const {
    data: { data },
  } = await axios.get(`/invoices/provider/${provider_id}`);
  return data;
}

export async function getProviderInvoice(
  invoice_id: number
): Promise<ProviderInvoice> {
  const {
    data: { data, settings },
  } = await axios.get(`/invoices/provider/view/${invoice_id}`);

  return {
    ...data,
    // surface company settings on the invoice object for easier consumption
    settings,
  };
}

export async function startInvoiceRun(params?: {
  cutoffDate?: string | null;
  daysCount?: number | null;
  isDryRun?: boolean;
}) {
  const queryParams: Record<string, unknown> = {};
  if (params?.cutoffDate) {
    queryParams.cutoff_date = params.cutoffDate;
  }
  if (
    typeof params?.daysCount === 'number' &&
    !Number.isNaN(params.daysCount)
  ) {
    queryParams.days_count = params.daysCount;
  }
  if (typeof params?.isDryRun === 'boolean') {
    queryParams.isDryRun = params.isDryRun;
  }

  const {
    data: { data },
  } = await axios.get(`/invoices/run/start`, {
    params: queryParams,
  });
  return data;
}

export async function updateInvoice(
  invoiceId: number,
  invoiceDto: Partial<ProviderInvoice>
): Promise<ProviderInvoice> {
  const {
    data: { data },
  } = await axios.put(`/invoices/${invoiceId}`, invoiceDto);
  return data;
}

//
export async function regenerateInvoiceRun(id: number) {
  const {
    data: { data },
  } = await axios.get(`/invoices/regenerate/run/${id}`);
  return data;
}
export async function regenerateInvoice(id: number) {
  const {
    data: { data },
  } = await axios.get(`/invoices/regenerate/invoice/${id}`);
  return data;
}
